/**
 * Cleans Google click/linker tracking parameters from URLs with hash routing.
 * Handles URLs like: https://domain.com/?_gl=...#/review/...
 * Converts them to: https://domain.com/#/review/...
 *
 * Only the Google params below are stripped — every other query param is
 * preserved. This matters because the public approvals demo is activated by
 * `?demo=1` (see demo/demoMode.ts) and attributed by `utm_*` (see
 * demo/demoAttribution.ts). An earlier version blanked the *entire* query
 * string, which wiped `?demo=1` on any reload/refresh and silently dropped the
 * visitor out of demo mode — the real review key `demo` then leaked to the live
 * API and 404'd ("review URL is no longer active").
 *
 * Runs only when a hash route is already present (the original guard): on the
 * very first landing the HashRouter hasn't installed a hash yet, so there is
 * nothing to clean around.
 */
const GOOGLE_TRACKING_PARAMS = [
  '_gl',
  '_ga',
  '_gac',
  'gclid',
  'gclsrc',
  'dclid',
  'gbraid',
  'wbraid',
]

export const cleanGoogleTrackingParams = (): boolean => {
  const url = new URL(window.location.href)

  // Only act once the hash route exists and there is a query to inspect.
  if (!url.search || !url.hash) {
    return false // No cleaning needed
  }

  let removedAny = false
  for (const param of GOOGLE_TRACKING_PARAMS) {
    if (url.searchParams.has(param)) {
      url.searchParams.delete(param)
      removedAny = true
    }
  }

  if (!removedAny) {
    return false // No Google params present — leave demo=1 / utm_* untouched
  }

  // Replace the current URL without causing a page reload
  window.history.replaceState(null, '', url.toString())

  return true // URL was cleaned
}
